/**
 * LLM pricing table — UI-side mirror of constantine's lookup_pricing()
 * at sdk/python/constantine_sdk/llm.py + dist/model-pricing.json (LiteLLM-
 * derived, ~2000 SKUs). Source of truth lives in the constantine repo at
 * the pinned commit (currently 3f5a88d → f7bb2da bump pending). Whenever
 * the constantine SHA in backend/docker/sidecars/sidecar.dockerfile bumps:
 *
 *   1. Re-sync MODEL_PRICING + FAMILY_PRICING below from llm.py
 *   2. Re-vendor fixtures/model-pricing.json from constantine/dist/
 *
 * Lookup order mirrors lookup_pricing() exactly:
 *   1. Exact match in MODEL_PRICING (hand-curated overrides)
 *   2. "self-hosted/*" prefix → zero pricing
 *   3. Strip everything before the last "/" and retry MODEL_PRICING
 *   4. Family-substring fallback against FAMILY_PRICING
 *   5. LiteLLM-derived dict (model-pricing.json)
 *   6. LiteLLM dict after one prefix-strip
 *   7. DEFAULT_PRICING + emit a console.warn once per unknown model
 *
 * NOTE — no pricing overlay system. The 3x bedrock multiplier that lived
 * here previously was reverse-engineered from a constantine SDK bug
 * where Bedrock SKUs missed the MODEL_PRICING dict and fell through to
 * the $3/$15 Sonnet-class default — making it look like Bedrock had a
 * ~3x surcharge. Real Bedrock-vs-direct premium is ~10% per the LiteLLM
 * table. When a user runs on bedrock, the model_id should already be the
 * routed form (`bedrock/us.anthropic...-v1:0`) and the lookup chain
 * resolves to the correct Bedrock-class rate.
 */

import litellmPricingRaw from './fixtures/model-pricing.json';
import modelRoutingRaw from './fixtures/model-routing.json';

interface ModelRoutingFile {
  version: string;
  generated_at: string;
  _semantics?: string;
  /** provider → logical-model-id → routed-SKU map. */
  providers: Record<string, Record<string, string>>;
}

const modelRouting = modelRoutingRaw as unknown as ModelRoutingFile;

/**
 * Map a logical model_id (as authored in module.yaml) to the routed
 * SKU the constantine runtime actually calls. Looks up the constantine-
 * shipped routing table at fixtures/model-routing.json (generated by
 * constantine `tools/build_model_routing.py` from LiteLLM data).
 *
 * Semantics — exact mirror of the constantine table's `_semantics`
 * declaration:
 *   - **missing-key-passes-bare**: when provider isn't in the table OR
 *     logical_id has no override for that provider, pass the bare
 *     string through unchanged. Caller's pricing lookup absorbs it.
 *   - **additive-override-not-whitelist**: the table NEVER invalidates
 *     a model_id. Custom user-provided SKUs (e.g. an unusual
 *     `bedrock/foo/bar` route in an uploaded YAML) flow straight
 *     through to LiteLLM lookup.
 *
 * Replaces the prior multi-attempt suffix-probing implementation. The
 * old approach tried `us.anthropic.<sku>-v1:0`, `us.anthropic.<sku>`,
 * and bare in sequence because LiteLLM's naming is inconsistent on the
 * `-v1:0` suffix. With the routing table, the constantine side owns
 * that mapping authoritatively; UI is single-attempt.
 */
export function resolveModelId(logical: string, provider?: string): string {
  if (!provider) return logical;
  const providerTable = modelRouting.providers?.[provider];
  if (!providerTable) return logical;
  return providerTable[logical] ?? logical;
}

export interface ModelPricing {
  /** USD per million input tokens. */
  input: number;
  /** USD per million output tokens. */
  output: number;
}

interface LiteLLMEntry {
  /** input USD per MTok */
  i: number;
  /** output USD per MTok */
  o: number;
  /** cache write USD per MTok (optional) */
  ci?: number;
  /** cache read USD per MTok (optional) */
  cr?: number;
  /** provider classification (informational) */
  p?: string;
}

interface LiteLLMPricingFile {
  generated_at: string;
  litellm_version: string;
  models: Record<string, LiteLLMEntry>;
}

const litellmPricing = litellmPricingRaw as unknown as LiteLLMPricingFile;
const litellmModels = litellmPricing.models;

/**
 * Hand-curated rate table — overrides the LiteLLM fallback for models where
 * constantine has confirmed up-to-date pricing or where LiteLLM lags. Stays
 * in sync with constantine's sdk/python/constantine_sdk/llm.py MODEL_PRICING.
 */
const MODEL_PRICING: Record<string, ModelPricing> = {
  // Anthropic direct
  'claude-haiku-4-5-20251001': { input: 1.0, output: 5.0 },
  'claude-haiku-4-5': { input: 1.0, output: 5.0 },
  'claude-sonnet-4-5-20250929': { input: 3.0, output: 15.0 },
  'claude-sonnet-4-6': { input: 3.0, output: 15.0 },
  // Opus 4.7 mirrors Opus 4.6's $5/$25 base rate. AWS Bedrock pricing page
  // (https://aws.amazon.com/bedrock/pricing/) does not yet publish a
  // distinct Opus 4.7 listing as of 2026-05; the LiteLLM vendored fixture
  // has the direct-API SKU at $5/$25 and applies a ~10% Bedrock multiplier
  // to `us.anthropic.claude-opus-4-7` (5.5/27.5). Keeping the hand-curated
  // override at $5/$25 matches constantine's MODEL_PRICING policy of pricing
  // by direct-API base rate; FAMILY_PRICING below captures regional spellings.
  // Source of truth: sdk/python/constantine_sdk/llm.py:24-33 (MODEL_PRICING).
  'claude-opus-4-7': { input: 5.0, output: 25.0 },
  'claude-opus-4-6': { input: 5.0, output: 25.0 },
  'claude-opus-4-5': { input: 5.0, output: 25.0 },

  // AWS Bedrock — base names (priced after stripping `bedrock/` prefix)
  'us.meta.llama3-1-8b-instruct-v1:0': { input: 0.22, output: 0.22 },
  'us.meta.llama3-1-70b-instruct-v1:0': { input: 0.72, output: 0.72 },
  'us.deepseek.r1-v1:0': { input: 1.35, output: 5.4 },
  'us.deepseek.v3-2-v1:0': { input: 0.62, output: 1.85 },
  'us.amazon.nova-pro-v1:0': { input: 0.8, output: 3.2 },
  'us.amazon.nova-lite-v1:0': { input: 0.06, output: 0.24 },
  'us.amazon.nova-micro-v1:0': { input: 0.035, output: 0.14 },
  'zai.glm-4.7': { input: 0.2, output: 0.6 },
  'zai.glm-5-v1:0': { input: 0.6, output: 1.8 },
  // Moonshot AI Kimi K2.5 on Bedrock — module.yaml defaults for
  // scored-repo / scored-functions emit `bedrock/kimi-k2.5`, which is
  // the constantine-side short alias for `bedrock/moonshotai.kimi-k2.5`
  // (the LiteLLM-canonical key). Both spellings must price identically
  // since the base-name strip (`bedrock/<x>` → `<x>`) collapses them on
  // the second lookup pass. Rates per AWS Bedrock pricing page
  // (us-east-1/us-east-2/us-west-2 standard tier, 2026-05):
  //   https://aws.amazon.com/bedrock/pricing/
  'kimi-k2.5': { input: 0.6, output: 3.0 },
  'moonshotai.kimi-k2.5': { input: 0.6, output: 3.0 },
};

/**
 * Family-substring fallback for SKUs that aren't in MODEL_PRICING and miss
 * the LiteLLM table. Ordered longest-first so specific variants match
 * before broader names.
 *
 * The Anthropic Claude block catches regional Bedrock spellings (e.g.
 * `us.anthropic.claude-opus-4-7`, `eu.anthropic.claude-sonnet-4-6`) that
 * the LiteLLM table prices ~10% higher due to the Bedrock multiplier;
 * we hold the direct-API rate here so the UI cost preview matches
 * constantine's runtime billing semantics. Cache rates layer on from
 * LiteLLM via withLiteLLMCache(). Mirrors constantine's FAMILY_PRICING
 * at sdk/python/constantine_sdk/llm.py:126-151 (Anthropic block is
 * UI-only — constantine relies on its own MODEL_PRICING + LiteLLM JSON).
 */
const FAMILY_PRICING: Array<readonly [string, ModelPricing]> = [
  // Anthropic Claude — versioned family substrings (longest-first within block)
  ['claude-opus-4-7', { input: 5.0, output: 25.0 }],
  ['opus-4-7', { input: 5.0, output: 25.0 }],
  ['claude-opus-4-6', { input: 5.0, output: 25.0 }],
  ['opus-4-6', { input: 5.0, output: 25.0 }],
  ['claude-sonnet-4-6', { input: 3.0, output: 15.0 }],
  ['sonnet-4-6', { input: 3.0, output: 15.0 }],
  ['claude-haiku-4-5', { input: 1.0, output: 5.0 }],
  ['haiku-4-5', { input: 1.0, output: 5.0 }],
  // Llama 3.x
  ['llama-3.3-405b', { input: 3.5, output: 3.5 }],
  ['llama-3.3-70b', { input: 0.5, output: 0.8 }],
  ['llama-3.3-8b', { input: 0.05, output: 0.1 }],
  ['llama-3.1-405b', { input: 3.5, output: 3.5 }],
  ['llama-3.1-70b', { input: 0.72, output: 0.72 }],
  ['llama-3.1-8b', { input: 0.18, output: 0.18 }],
  // Qwen 2.5
  ['qwen2.5-coder-32b', { input: 0.3, output: 0.5 }],
  ['qwen2.5-coder-7b', { input: 0.07, output: 0.16 }],
  ['qwen2.5-72b', { input: 0.9, output: 0.9 }],
];

const SELF_HOSTED_PRICING: ModelPricing = { input: 0, output: 0 };

/**
 * Fallback when an unknown model id is encountered. Matches constantine's
 * DEFAULT_PRICING. UI logs once per unknown model so the next manifest
 * bump surfaces missing pricing immediately.
 */
const DEFAULT_PRICING: ModelPricing = { input: 3.0, output: 15.0 };

const _seenUnknown = new Set<string>();

/**
 * Model options for the per-module config editor's `model_ref` dropdown.
 *
 * Source: `fixtures/bifrost-allowed-models.json`, which is generated from
 * `backend/cf-templates/bifrost/template.yml` (the Bedrock provider's
 * keys[].models list — i.e. exactly what the Bifrost dev/prod stack will
 * accept from a routing request). Re-extract via:
 *
 *   npm run extract:bifrost-models
 *
 * Single source of truth — no hand-maintained TS list to drift. The only
 * additions on top of the JSON are the three tier references
 * (`fast` / `standard` / `deep`), which are pipeline-doc-level aliases
 * resolved by the cost evaluator at eval time, not Bifrost SKUs.
 *
 * For models that aren't in Bifrost's allow-list but the user has
 * authored manually (custom YAML uploads, future bumps), the editor
 * tolerates the unknown value by surfacing it as a "Custom" group at
 * the top of the dropdown so user config doesn't get silently dropped.
 *
 * Long-term: a `/bifrost/models` HTTP endpoint that reads the live
 * tenant's VirtualKey.provider_configs[].allowed_models is more
 * accurate (per-tenant overrides, mid-day reseller swaps). That's a
 * backend Go PR; until then the build-time CF extract is faithful to
 * what the running stack accepts.
 */
import bifrostAllowedRaw from './fixtures/bifrost-allowed-models.json';

export interface ModelOption {
  /** Group label shown in the SelectGroup header. */
  group: string;
  /** Wire value — what gets written to module_config. */
  value: string;
  /** Optional human-readable subtitle. */
  hint?: string;
}

interface BifrostAllowedModels {
  source: string;
  extracted_at: string;
  note: string;
  models: string[];
}

const bifrostAllowed = bifrostAllowedRaw as BifrostAllowedModels;

/**
 * Group a routed model SKU into a sub-group based on its prefix/vendor.
 * Keeps the dropdown skimmable when Bifrost's allow-list has 15+ entries
 * across multiple providers (OpenAI, Bedrock with Anthropic/Amazon/etc.).
 */
function bedrockGroupFor(model: string): string {
  // Non-Bedrock providers (OpenRouter, OpenAI direct, Azure, ...) — these
  // appear in Bifrost's openai/anthropic/openrouter provider blocks.
  if (model.startsWith('openai/')) return 'OpenAI';
  if (model.startsWith('openrouter/')) return 'OpenRouter';
  if (model.startsWith('azure_ai/')) return 'Azure';
  if (model.startsWith('databricks/')) return 'Databricks';
  if (model.startsWith('self-hosted/')) return 'Self-hosted';

  // Bedrock SKUs are bare names (priced after stripping any `bedrock/`
  // prefix). Sub-group by vendor segment.
  if (model.includes('anthropic')) return 'Bedrock · Anthropic';
  if (model.startsWith('amazon.') || model.startsWith('us.amazon.')) {
    return 'Bedrock · Amazon';
  }
  if (model.startsWith('deepseek.')) return 'Bedrock · DeepSeek';
  if (model.startsWith('mistral.')) return 'Bedrock · Mistral';
  if (model.startsWith('google.')) return 'Bedrock · Google';
  if (model.startsWith('zai.')) return 'Bedrock · Z.AI';
  return 'Bedrock';
}

function hintFor(model: string): string | undefined {
  const pricing = lookupPricingExact(model);
  if (!pricing) return undefined;
  return `$${pricing.input.toFixed(2)} / $${pricing.output.toFixed(2)} per MTok`;
}

/**
 * Return model options for the editor's model_ref dropdown.
 *   1. Pipeline-tier references (fast/standard/deep)
 *   2. Every entry from bifrost-allowed-models.json, grouped by vendor
 */
export function getCuratedModelOptions(): ModelOption[] {
  const options: ModelOption[] = [
    {
      group: 'Tier reference',
      value: 'fast',
      hint: 'Resolves to pipeline.llm.models.fast',
    },
    {
      group: 'Tier reference',
      value: 'standard',
      hint: 'Resolves to pipeline.llm.models.standard',
    },
    {
      group: 'Tier reference',
      value: 'deep',
      hint: 'Resolves to pipeline.llm.models.deep',
    },
  ];
  // Dedup while preserving first-seen order (CF template can have repeats).
  const seen = new Set<string>();
  for (const model of bifrostAllowed.models) {
    if (seen.has(model)) continue;
    seen.add(model);
    options.push({
      group: bedrockGroupFor(model),
      value: model,
      hint: hintFor(model),
    });
  }
  return options;
}

function liteLLMLookup(key: string): ModelPricing | null {
  const entry = litellmModels[key];
  if (!entry) return null;
  return { input: entry.i, output: entry.o };
}

function lookupPricingExact(model: string): ModelPricing | null {
  if (model in MODEL_PRICING) return MODEL_PRICING[model];
  if (model.startsWith('self-hosted/')) return SELF_HOSTED_PRICING;
  const base = model.includes('/') ? model.split('/').pop()! : undefined;
  if (base && base in MODEL_PRICING) return MODEL_PRICING[base];

  const low = model.toLowerCase();
  for (const [family, pricing] of FAMILY_PRICING) {
    if (low.includes(family)) return pricing;
  }

  const lite = liteLLMLookup(model);
  if (lite) return lite;
  if (base) {
    const liteBase = liteLLMLookup(base);
    if (liteBase) return liteBase;
  }
  return null;
}

/**
 * Pricing entry that also exposes cache-tier rates. The base
 * `ModelPricing` exports keep input/output for backwards-compat with
 * callers that don't model cache; `ModelPricingFull` is what
 * tokensToCost reads internally.
 */
export interface ModelPricingFull extends ModelPricing {
  /** USD per million cache-write (a.k.a. cache_creation) tokens. */
  cacheCreation: number;
  /** USD per million cache-read (a.k.a. cache_read) tokens. */
  cacheRead: number;
}

function liteLLMLookupFull(key: string): ModelPricingFull | null {
  const entry = litellmModels[key];
  if (!entry) return null;
  return {
    input: entry.i,
    output: entry.o,
    cacheCreation: entry.ci ?? 0,
    cacheRead: entry.cr ?? 0,
  };
}

/**
 * Merge a hand-curated MODEL_PRICING entry (constantine's overrides for
 * base input/output rates) with cache fields from the LiteLLM table —
 * constantine doesn't track ci/cr in MODEL_PRICING, so cache rates
 * always come from LiteLLM. Without this merge, models in MODEL_PRICING
 * would always project cache_creation/cache_read as $0.
 */
function withLiteLLMCache(model: string, base: ModelPricing): ModelPricingFull {
  const lite = liteLLMLookupFull(model);
  return {
    input: base.input,
    output: base.output,
    cacheCreation: lite?.cacheCreation ?? 0,
    cacheRead: lite?.cacheRead ?? 0,
  };
}

function lookupPricingFullExact(model: string): ModelPricingFull | null {
  if (model in MODEL_PRICING) {
    return withLiteLLMCache(model, MODEL_PRICING[model]);
  }
  if (model.startsWith('self-hosted/')) {
    return { ...SELF_HOSTED_PRICING, cacheCreation: 0, cacheRead: 0 };
  }
  const base = model.includes('/') ? model.split('/').pop()! : undefined;
  if (base && base in MODEL_PRICING) {
    return withLiteLLMCache(base, MODEL_PRICING[base]);
  }

  const low = model.toLowerCase();
  for (const [family, pricing] of FAMILY_PRICING) {
    if (low.includes(family)) {
      // FAMILY_PRICING is a broad substring fallback; if any LiteLLM
      // entry matches the original SKU, prefer its cache fields.
      return withLiteLLMCache(model, pricing);
    }
  }

  const lite = liteLLMLookupFull(model);
  if (lite) return lite;
  if (base) {
    const liteBase = liteLLMLookupFull(base);
    if (liteBase) return liteBase;
  }
  return null;
}

export function lookupPricing(model: string, provider?: string): ModelPricing {
  const routed = resolveModelId(model, provider);
  const hit = lookupPricingFullExact(routed);
  if (hit) return hit;
  if (!_seenUnknown.has(routed)) {
    _seenUnknown.add(routed);
    console.warn(
      `[pipelineEditor] no pricing data for model=${JSON.stringify(routed)}` +
        (provider ? ` (provider=${provider})` : '') +
        `; falling back to $${DEFAULT_PRICING.input.toFixed(2)}/$${DEFAULT_PRICING.output.toFixed(2)} per MTok`
    );
  }
  return DEFAULT_PRICING;
}

/**
 * Compute USD cost for a token tuple including optional cache tiers.
 *   - inputTokens:          fresh-input tokens
 *   - outputTokens:         output tokens
 *   - cacheCreationTokens:  optional, "cache_write" — billed at provider's
 *                           write multiplier (typically 1.25× base input
 *                           for Anthropic; 1.5× for Gemini; etc).
 *   - cacheReadTokens:      optional, "cache_read" — billed at provider's
 *                           read multiplier (typically 0.10× base input
 *                           for Anthropic; 0.25× for Gemini).
 *
 * Provider-portable: per-provider multipliers come from the LiteLLM
 * model-pricing table (ci/cr fields). Modules whose rate object omits
 * the cache fields pass 0 here and behavior is identical to base
 * input+output pricing.
 */
export function tokensToCost(
  inputTokens: number,
  outputTokens: number,
  modelId: string,
  provider?: string,
  cacheCreationTokens: number = 0,
  cacheReadTokens: number = 0
): number {
  const routed = resolveModelId(modelId, provider);
  const full = lookupPricingFullExact(routed);
  const pricing = full ?? {
    ...DEFAULT_PRICING,
    cacheCreation: 0,
    cacheRead: 0,
  };
  if (!full && !_seenUnknown.has(routed)) {
    _seenUnknown.add(routed);
    console.warn(
      `[pipelineEditor] no pricing data for model=${JSON.stringify(routed)}` +
        (provider ? ` (provider=${provider})` : '') +
        `; falling back to $${DEFAULT_PRICING.input.toFixed(2)}/$${DEFAULT_PRICING.output.toFixed(2)} per MTok`
    );
  }
  return (
    (inputTokens / 1_000_000) * pricing.input +
    (outputTokens / 1_000_000) * pricing.output +
    (cacheCreationTokens / 1_000_000) * pricing.cacheCreation +
    (cacheReadTokens / 1_000_000) * pricing.cacheRead
  );
}
